/**
 * Session replay — public entry point.
 *
 * A replay starts one of two ways. startSessionReplay() asks the server at app
 * boot, and the server answers yes or no: yes continuously on a machine that
 * can afford it, for the first SLOW_DEVICE_REPLAY_LIMIT boots on one that
 * cannot, and always for an account carrying the `replay_force` flag — which is
 * how support gets a recording out of any machine on any EMR. Which machine is
 * which is the server's call too: this side only reports what it can see
 * (collectDeviceProfile).
 *
 * The bug button is the other way in. If nothing is recording when a report
 * starts, it asks the server for a replay of that reproduction and keeps it
 * running for BUG_REPORT_TAIL_MS after the screen capture stops. A boot inside
 * that window asks again, so a closed side panel does not end it.
 *
 * The decision goes through the SERVER. The browser never decides on its
 * own whether to record — there is no client-side flag that can be flipped by a
 * stale bundle, a cached localStorage value, or an extension update that shipped
 * late. If /v1/replay/policy is unreachable, nothing records.
 */

import recorder from './recorder';
import { notePageContext } from './sidecar';
import { collectDeviceProfile } from './deviceProfile';
import { installConsoleTap } from './consoleTap';
import { clearCachedChunk, uploadCachedChunk } from './chunkCache';
import { startEmrDomPump, stopEmrDomPump } from './emrDomPump';

let bootstrapped = false;
let currentRoute = null;
// Bumped by discardSessionReplay(). A policy call or rrweb import that was in
// flight when the user logged out must not start recording for the next user.
let sessionGeneration = 0;
// The replay THIS module started to record a bug reproduction, if any. Only a
// replay we started is ours to stop when its tail runs out — and only while the
// recorder is still on it, since the recorder's own limits stop recordings
// without telling anyone here.
let bugReportReplayId = null;
let bugReportStart = null;
let bugReportTimer = null;
let bugReportApiClient = null;

function browserRoute() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

/** Keep replay page context aligned with React Router, including MemoryRouter. */
export function setReplayRoute(route) {
  const nextRoute = String(route || '');
  if (!nextRoute || nextRoute === currentRoute) return;
  currentRoute = nextRoute;
  if (recorder.isRecording) {
    notePageContext({ url: currentRoute, surface: detectSurface() });
  }
}

function detectSurface() {
  // The extension serves the app from chrome-extension: (side panel) or from
  // embed.comprehendpt.com inside an iframe; the web app from app.*.
  if (typeof window === 'undefined') return 'web';
  if (window.location.protocol === 'chrome-extension:') return 'cx';
  if (window.top !== window.self) return 'cx';
  return 'web';
}

/** What /v1/replay/policy is told about this load. */
function policyContext(apiClient) {
  return {
    surface: detectSurface(),
    emr: apiClient.EMR || null,
    appVersion: document.getElementById('APP_VERSION')?.textContent || null,
    firstUrl: currentRoute || browserRoute(),
    // What this machine looks like, for the server to class it. Reported, not
    // acted on: a browser saying "I am fast" still gets whatever the server
    // decides, and a browser that says nothing is not assumed to be slow.
    device: collectDeviceProfile(),
  };
}

/** Bring the sidecars up on a recorder that just started. */
function attachSidecars(apiClient) {
  // The recorder has to be live before a custom event can be emitted.
  installConsoleTap();
  notePageContext({
    emr: apiClient.EMR || null,
    url: currentRoute || browserRoute(),
    surface: detectSurface(),
  });
  // On the EMR surfaces, also pull the EMR page's own DOM recording in.
  if (detectSurface() === 'cx') startEmrDomPump();
}

/**
 * Ask the server whether to record this app load, and start if so.
 *
 * Call once, after login. Idempotent, never throws, and returns false for every
 * "no" — including every failure — so a caller can treat the return value as
 * "is a replay running".
 */
export async function startSessionReplay(apiClient) {
  if (bootstrapped) return recorder.isRecording;
  bootstrapped = true;

  if (!apiClient || !apiClient.sessionKey) return false;

  // Whatever the last close left behind, before deciding anything about this
  // load: it belongs to a replay the server already authorised, so it goes up
  // even when the answer below is "do not record". Not awaited — an old chunk
  // must never delay a new recording, or block it if the upload hangs.
  uploadCachedChunk(apiClient).catch(() => {});

  const generation = sessionGeneration;
  try {
    const policy = await apiClient.getReplayPolicy(policyContext(apiClient));
    if (generation !== sessionGeneration) return false;

    if (!policy || !policy.record || !policy.replayId) {
      // A boot the server declines only by default — a slow machine past its
      // allowance, or an EMR that is off by default — still records through
      // the tail of a bug report made just before it.
      if (policy && DECLINED_BY_DEFAULT_REASONS.has(policy.reason)) {
        return resumeBugReportTail(apiClient);
      }
      return false;
    }
    const started = await recorder.start(apiClient, policy);
    if (generation !== sessionGeneration) {
      // Logged out while rrweb was loading: this recording is nobody's.
      if (recorder.replayId === policy.replayId) recorder.stopWithoutUpload();
      return false;
    }
    if (started) attachSidecars(apiClient);
    return started;
  } catch (e) {
    // Includes the 299 "invalid session" path — a user whose session just
    // expired should get the login screen, not a recorder that keeps retrying.
    console.log('replay: policy check failed, not recording', e);
    return false;
  }
}

/** The replay currently being recorded, or null. */
export function getActiveReplayId() {
  return recorder.isRecording && !recorder.stopping ? recorder.replayId : null;
}

/** Enough context for a bug report to identify and seek within this replay. */
export function getActiveReplayMetadata() {
  if (!recorder.isRecording || recorder.stopping || !recorder.startedAt) return null;
  const capturedAt = Date.now();
  return {
    replayId: recorder.replayId,
    replayStartedAt: new Date(recorder.startedAt).toISOString(),
    capturedAt: new Date(capturedAt).toISOString(),
    offsetMs: Math.max(0, capturedAt - recorder.startedAt),
  };
}

// Sidecar emitters, re-exported so callers depend on the replay module rather
// than reaching into it. All are no-ops when nothing is recording, and the
// content-bearing ones are additionally server-gated (see sidecar.js).
export {
  notePageContext,
  noteMessage,
  noteApiCall,
  noteEmrDomBatch,
  noteConsole,
  SIDECAR_TAGS,
} from './sidecar';

/** Called by the error boundary so the viewer can rank replays by error count. */
export function noteReplayError() {
  if (recorder.isRecording) recorder.noteError();
}

/**
 * Record the reproduction a bug report is about to capture.
 *
 * The bug reporter's flow is: press the button, reproduce the bug on camera,
 * then submit — so the moments worth watching happen AFTER this is called, and
 * a recording started here covers exactly the window the video covers.
 *
 * Two outcomes, both fine. If a replay is already running, it is used as-is:
 * better, because it also holds the minutes leading up to the report. If not —
 * usually a machine the server declined to record continuously — the server is
 * asked for one specifically for this reproduction, which it grants regardless
 * of device class or EMR. Only the second kind is ever stopped by this module:
 * it runs for BUG_REPORT_TAIL_MS after the screen capture ends
 * (endBugReportCapture), whether or not the report is then sent.
 *
 * Never throws and never blocks the bug reporter: no replay is a worse report,
 * not a broken one.
 */
export async function startBugReportReplay(apiClient) {
  const running = getActiveReplayMetadata();
  if (running) return running;
  // Recording, but not usably: a stop is draining its last chunk. Asking for a
  // replay now would create a row the recorder then declines to record into.
  if (recorder.isRecording) return null;
  if (!apiClient || !apiClient.sessionKey) return null;

  // Bounded for the same reason checkpointForBugReport is: the caller awaits
  // this before starting the screen capture, and the API client sets no timeout
  // of its own, so a policy call that hangs on clinic Wi-Fi would leave the user
  // pressing record and watching nothing happen. A late answer still starts the
  // recording — it just stops holding up the reproduction.
  // One request per report however many times the button is pressed, and a
  // handle the finish path can wait on when the race below gives up early.
  if (!bugReportStart) {
    bugReportStart = beginBugReportReplay(apiClient).finally(() => {
      bugReportStart = null;
    });
  }
  return Promise.race([
    bugReportStart,
    new Promise((resolve) => {
      setTimeout(() => resolve(null), BUG_REPORT_START_TIMEOUT_MS);
    }),
  ]);
}

const BUG_REPORT_START_TIMEOUT_MS = 3000;

/**
 * Longest a reproduction may record before it stops itself.
 *
 * A report can be abandoned in ways this module never hears about — the user
 * cancels the screen-capture picker, the injector never answers, they walk away
 * — and the extension only reports a capture that FINISHED. Without this, one
 * abandoned report records until the tab closes or the 2-hour chunk cap, on a
 * machine the server may have declined to record continuously. A capture that
 * does finish replaces this with the tail below.
 */
const BUG_REPORT_MAX_MS = 15 * 60 * 1000;

/**
 * How long a bug-report recording keeps going after the screen capture stops.
 *
 * The bug is usually still happening: the user stops the capture and keeps
 * working in the state that produced it, and those minutes are routinely the
 * ones support wants. Persisted per account so a boot inside the window
 * records too — in the extension the side panel is closed and reopened
 * constantly, and in the embed a navigation reloads the whole app.
 */
const BUG_REPORT_TAIL_MS = 10 * 60 * 1000;
const BUG_REPORT_WINDOW_KEY = 'replay_bug_report_until';
// The server's "no" answers that a bug report overrides (see decide_replay_policy).
const DECLINED_BY_DEFAULT_REASONS = new Set(['slow_device_limit', 'emr_default_off', 'user_disabled']);

function rememberBugReportWindow(apiClient, until) {
  try {
    if (!apiClient?.sessionEmail) return;
    window.localStorage.setItem(
      BUG_REPORT_WINDOW_KEY,
      JSON.stringify({ email: apiClient.sessionEmail, until }),
    );
  } catch (e) {
    /* storage unavailable: the tail still runs in this page */
  }
}

/** Milliseconds left in this account's bug-report window, or 0. */
function readBugReportWindow(apiClient) {
  try {
    const raw = window.localStorage.getItem(BUG_REPORT_WINDOW_KEY);
    const record = raw ? JSON.parse(raw) : null;
    if (!record || !apiClient?.sessionEmail || record.email !== apiClient.sessionEmail) return 0;
    return Math.max(0, Number(record.until) - Date.now());
  } catch (e) {
    return 0;
  }
}

/**
 * A boot inside the tail of a recent bug report, on a machine the server just
 * declined: ask again as a bug report, and stop when the window would have.
 */
async function resumeBugReportTail(apiClient) {
  const remaining = readBugReportWindow(apiClient);
  if (!remaining || bugReportStart) return false;
  bugReportStart = beginBugReportReplay(apiClient, remaining).finally(() => {
    bugReportStart = null;
  });
  return Boolean(await bugReportStart);
}

async function beginBugReportReplay(apiClient, stopAfterMs = BUG_REPORT_MAX_MS) {
  const generation = sessionGeneration;
  try {
    const policy = await apiClient.getReplayPolicy({
      kind: 'bug_report',
      ...policyContext(apiClient),
    });
    if (generation !== sessionGeneration) return null;
    if (!policy || !policy.record || !policy.replayId) return null;
    if (!(await recorder.start(apiClient, policy))) return null;
    if (generation !== sessionGeneration) {
      if (recorder.replayId === policy.replayId) recorder.stopWithoutUpload();
      return null;
    }
    // start() returns true for "already recording" too, so the only proof this
    // replay is ours to stop is that the recorder is now on the id we asked for.
    // If the boot policy won the race, adopt what it started instead — the row
    // we just created stays empty and expires with its TTL.
    if (recorder.replayId !== policy.replayId) return getActiveReplayMetadata();

    // A self-stop armed for an earlier report — one the recorder has since
    // stopped on its own — must not fire into this one.
    clearTimeout(bugReportTimer);
    bugReportReplayId = policy.replayId;
    bugReportApiClient = apiClient;
    bugReportTimer = setTimeout(() => {
      finishBugReportReplay(apiClient).catch(() => {});
    }, stopAfterMs);
    attachSidecars(apiClient);
    return getActiveReplayMetadata();
  } catch (e) {
    console.log('replay: could not start a recording for this bug report', e);
    return null;
  }
}

/**
 * The screen capture behind a bug report has stopped. From here a recording
 * this module started for it runs on for BUG_REPORT_TAIL_MS and then stops; a
 * recording that was already running when the report began is not touched.
 * A second capture inside the tail simply extends it.
 */
export async function endBugReportCapture() {
  // A start still in flight — the race in startBugReportReplay gave up on it —
  // is what the tail has to be measured from once it lands.
  if (bugReportStart) await bugReportStart.catch(() => {});
  if (bugReportReplayId === null) return;
  const apiClient = bugReportApiClient;
  rememberBugReportWindow(apiClient, Date.now() + BUG_REPORT_TAIL_MS);
  clearTimeout(bugReportTimer);
  bugReportTimer = setTimeout(() => {
    finishBugReportReplay(apiClient).catch(() => {});
  }, BUG_REPORT_TAIL_MS);
}

/**
 * Stop a recording started for a bug report. A replay that was already running
 * when the report began is left alone — it is this session's recording, not
 * this report's.
 */
async function finishBugReportReplay(apiClient) {
  if (bugReportStart) await bugReportStart.catch(() => {});
  if (bugReportReplayId === null) return;
  const owned = bugReportReplayId;
  bugReportReplayId = null;
  clearTimeout(bugReportTimer);
  bugReportTimer = null;
  // The recorder may have moved on since: the buffer ceiling and the chunk cap
  // both stop it directly. Stopping whatever is running now would end a replay
  // that is not this report's.
  if (recorder.replayId !== owned) return;
  stopEmrDomPump();
  try {
    const summary = await recorder.stop();
    if (summary) await apiClient?.finalizeReplay?.({ ...summary, kind: 'bug_report' });
  } catch (e) {
    // The chunks are already uploaded; losing the summary row only costs the
    // viewer its event/error counts.
    console.log('replay: could not finalize the bug-report recording', e);
  }
}

/**
 * Flush a checkpoint for a bug report without ending the panel's replay.
 * Returns identifying/seek metadata, or null when nothing was recording.
 */
export async function checkpointForBugReport() {
  const metadata = getActiveReplayMetadata();
  if (!metadata) return null;

  // Hard ceiling on the whole thing. The caller awaits this before sending the
  // bug report, so anything that can hang here — a stalled chunk upload, a
  // wedged extension message — becomes "the user pressed Send and nothing
  // happened". Losing the replay attachment is an acceptable outcome; losing
  // the bug report is not.
  return Promise.race([
    recorder.flush().then(() => metadata).catch(() => metadata),
    new Promise((resolve) =>
      setTimeout(() => {
        console.log('replay: checkpoint timed out, sending the report anyway');
        resolve(metadata);
      }, CHECKPOINT_TIMEOUT_MS),
    ),
  ]);
}

const CHECKPOINT_TIMEOUT_MS = 15000;

/**
 * Tear down on logout / session swap.
 *
 * Deliberately does NOT call /v1/replay/finalize. This runs from
 * ApiClient.reset(), which clears sessionKey synchronously — any request we
 * started here would land after the credentials were gone and fail anyway.
 * The replay is left un-finalized, exactly like a closed tab, which the viewer
 * already handles by counting objects in S3 rather than trusting chunk_count.
 *
 * What matters here and is done synchronously enough to be reliable: stop
 * recording the moment the session ends, so nothing from the NEXT user on this
 * tab is appended to the previous user's replay.
 */
export function discardSessionReplay() {
  sessionGeneration += 1;
  try {
    recorder.stopWithoutUpload();
  } catch (e) {
    console.log('replay: discard failed', e);
  } finally {
    // Let the next user on this tab get their own policy decision instead of
    // silently inheriting the previous account's answer.
    stopEmrDomPump();
    bugReportReplayId = null;
    bugReportApiClient = null;
    clearTimeout(bugReportTimer);
    bugReportTimer = null;
    // The carried-over tail goes too. It is unmasked chart content, only this
    // account could have uploaded it, and a shared clinic workstation must not
    // keep it readable after they sign out.
    clearCachedChunk();
    bootstrapped = false;
  }
}
